import endpoints from '@app/src/api/endpoints'
import { useCreateResource, useUpdateResourceWithoutBody } from '@app/src/api/updateHooks'
import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import ControlledDateField from '@app/src/components/Form/ControlledDateField'
import LoadingButton from '@app/src/components/LoadingButton'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { ResponseItemView } from '@app/src/context/ResponseItemContextProvider'
import RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import type { AiSuggestion } from '@app/src/pages/Questionnaire/hooks/useFetchFileAiSuggestions'
import QuestionSuggestionFooter from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/QuestionSuggestionFooter'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import type { RequestItem, UploadReference } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import AttachmentIcon from '@mui/icons-material/Attachment'
import CropDinIcon from '@mui/icons-material/CropDin'
import { FormHelperText, Stack, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface Props {
  name: string
  id: number
  sourceObjectId: number
  sourceObjectType: string
  validationError: string
  requestItem: RequestItem
  required?: boolean
  disabled?: boolean
  isPreview: boolean
}

const DirectFileUploadField = ({
  name,
  id,
  validationError,
  required,
  disabled,
  sourceObjectId,
  sourceObjectType,
  isPreview,
  requestItem,
}: Props): JSX.Element => {
  const { register, setValue, control, clearErrors, watch, trigger } = useFormContext()
  const { formatMessage } = useIntl()
  const { showErrorNotification } = useErrorNotification()
  const { get } = useApi()
  const { spacing, palette } = useTheme()
  const { setResponseItemView, previousAnswer, hideInputField, setQuestionSkippedFields } = useResponseItemContext()
  const { applySkipRuleEffectsToQuestion } = useQuestionRules(requestItem)
  const { markRepositoryCopy } = useReport()

  const originalFileName = watch(`${name}.originalFileName`)
  const watchOriginalFileName = disabled ? '' : originalFileName
  const answerStatus = watch(`${id}.answerStatus`)

  const [isDownloadingSuggestedFile, setIsDownloadingSuggestedFile] = useState(false)

  const { mutate, isPending: isLoading } = useCreateResource<UploadReference, FormData>({
    options: {
      onError: error =>
        showErrorNotification({
          requestError: error,
          specificFallbackErrorMessage: formatMessage({ id: 'reporting.uploadFileError' }),
        }),
    },
  })

  const { mutateAsync: mutateCloneFile, isPending: isMutateCloneFileLoading } =
    useUpdateResourceWithoutBody<UploadReference>({
      method: 'put',
    })

  const onDrop = useCallback(
    async (acceptedFiles: Array<Blob>) => {
      if (isPreview) return

      const formData = new FormData()
      formData.append('file', acceptedFiles[0])
      mutate(
        { url: endpoints.fileUpload(sourceObjectId, sourceObjectType), body: formData },
        {
          onSuccess: ({ originalFileName }) => {
            setResponseItemView(prevState =>
              prevState === ResponseItemView.SuggestedAnswerAccepted ? ResponseItemView.ShowSuggestedAnswer : prevState,
            )
            setValue(`${name}.originalFileName`, originalFileName, { shouldValidate: true, shouldDirty: true })
          },
        },
      )
    },
    [name, setValue, applySkipRuleEffectsToQuestion, requestItem.questionId],
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })
  const displayValue = isDragActive
    ? formatMessage({ id: 'reporting.dropFile' })
    : watchOriginalFileName || formatMessage({ id: 'reporting.uploadFile' }) + (required ? ' *' : null)

  const setOriginalFileName = useCallback(
    (fileName?: string) => setValue(`${name}.originalFileName`, fileName, { shouldValidate: true, shouldDirty: true }),
    [name, setValue],
  )

  const onApply = async () => {
    if (!previousAnswer) return
    if (previousAnswer.cannotAnswer) {
      setQuestionSkippedFields()
      setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)
      await trigger()
      return
    }
    await mutateCloneFile(
      { url: endpoints.reuseFile(previousAnswer.id, sourceObjectId) },
      {
        onSuccess: async clonedFile => {
          setOriginalFileName(
            clonedFile?.originalFileName ?? [previousAnswer.uploadReferences].flat()[0]?.originalFileName,
          )
          setValue(`${id}.expiresAt`, previousAnswer?.expiresAt)
          setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)
          await trigger()
        },
      },
    )
  }

  const onApplyAiSuggestion = async (suggestion: AiSuggestion) => {
    if (isPreview) return

    const suggestedFileName = suggestion.response
    if (!suggestedFileName) return

    try {
      setIsDownloadingSuggestedFile(true)
      const { blob } = await get<{ blob: Blob; fileName: string }>(
        { url: endpoints.suggestionFile(suggestedFileName) },
        { isBlob: true },
      )

      const formData = new FormData()
      formData.append('file', new File([blob], suggestedFileName))

      mutate(
        { url: endpoints.fileUpload(sourceObjectId, sourceObjectType), body: formData },
        {
          onSuccess: async ({ originalFileName }) => {
            setOriginalFileName(originalFileName)
            markRepositoryCopy(sourceObjectId, originalFileName)
            setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)
            await trigger()
          },
        },
      )
    } catch (error) {
      showErrorNotification({
        requestError: error instanceof RequestError ? error : null,
        specificFallbackErrorMessage: formatMessage({ id: 'reporting.uploadFileError' }),
      })
    } finally {
      setIsDownloadingSuggestedFile(false)
    }
  }

  const onIgnoreSuggestion = () => {
    setOriginalFileName(undefined)
    setValue(`${id}.cannotAnswer`, false)
    setValue(`${id}.answerStatus`, AnswerStatus.Answered)
    setResponseItemView(ResponseItemView.ShowSuggestedAnswer)
  }

  useEffect(() => {
    if (!isPreview && disabled && originalFileName && answerStatus !== AnswerStatus.Excluded) {
      setOriginalFileName(undefined)
    }
  }, [disabled, isPreview, originalFileName, answerStatus, setOriginalFileName])

  useEffect(() => {
    if (watchOriginalFileName && requestItem.questionId) {
      applySkipRuleEffectsToQuestion(requestItem.questionId, [QuestionRulesConditions.FileUploaded])
    }
  }, [watchOriginalFileName, requestItem.questionId, applySkipRuleEffectsToQuestion])

  return (
    <>
      <Stack px={4} pb={isPreview ? 0 : 2} spacing={3} display={hideInputField ? 'none' : 'flex'}>
        <Stack>
          <Stack width="100%" direction="row" alignItems="center" spacing={2}>
            <Stack flex={1} minWidth={0} {...getRootProps()} data-testid="upload-file">
              <input type="hidden" name={`${name}.originalFileName`} ref={register()} disabled={disabled} />
              <input
                type="hidden"
                name={`${name}.sourceObjectId`}
                ref={register()}
                disabled={disabled}
                defaultValue={sourceObjectId}
              />
              <input
                type="hidden"
                name={`${name}.sourceObjectType`}
                ref={register()}
                disabled={disabled}
                defaultValue={sourceObjectType}
              />

              <input {...getInputProps()} disabled={disabled} />
              <LoadingButton
                name={`${name}.fileUpload`}
                variant="outlined"
                color="primary"
                fullWidth
                disabled={disabled}
                loading={isLoading || isDownloadingSuggestedFile}
                onClick={(): void => clearErrors(`${id}`)}
                endIcon={<AttachmentIcon fontSize="small" />}
                sx={{
                  borderColor: validationError ? palette.error.dark : undefined,
                  borderRadius: 1,
                  borderWidth: 0.5,
                  justifyContent: 'space-between',
                  '& > div': { minWidth: 0 },
                }}
              >
                <Typography variant="body1" noWrap textAlign="left">
                  {displayValue}
                </Typography>
              </LoadingButton>
            </Stack>

            <CropDinIcon
              fontSize="small"
              sx={{
                transform: 'rotate(45deg)',
              }}
            />
            <ControlledDateField
              fieldLabel={formatMessage({ id: 'reporting.expiresAt' })}
              control={control}
              name={`${id}.expiresAt`}
              disabled={disabled}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Stack>
          {validationError && (
            <FormHelperText
              sx={{
                color: palette.secondary.dark,
                paddingLeft: spacing(2),
              }}
              error
            >
              {validationError}
            </FormHelperText>
          )}
        </Stack>
      </Stack>
      <QuestionSuggestionFooter
        requestItem={requestItem}
        questionType={QuestionTypes.File}
        onApplyPreviousAnswer={onApply}
        onApplyAiSuggestion={onApplyAiSuggestion}
        onIgnoreSuggestion={onIgnoreSuggestion}
        isPreview={isPreview}
        isLoadingCloneFile={isMutateCloneFileLoading}
      />
    </>
  )
}

export default DirectFileUploadField
